import React from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { Link } from "react-router-dom";
import { ReactComponent as BuildingsFill } from "bootstrap-icons/icons/buildings-fill.svg";
import { ReactComponent as BoxSeam } from "bootstrap-icons/icons/box-seam.svg";
import { ReactComponent as People } from "bootstrap-icons/icons/people.svg";
import Spinner from "../spinner/Spinner";
import { useHeadOffices } from "../../hooks/useHeadOffice";
import CurrencyFormatter from "../../components/utils/CurrencyFormatter";

const HeadOfficeDashboard = () => {
  const { data, isLoading, error } = useHeadOffices();

  // console.log("Dashboard - Full response:", data);
  // console.log("Dashboard - Is Loading:", isLoading);
  // console.log("Dashboard - Error:", error);

  const headOffices = data?.data || [];
  // console.log("Dashboard - Head Offices:", headOffices);

  const totalHeadOffices = headOffices.length;
  const activeHeadOffices = headOffices.filter((ho) => ho.is_activated).length;
  const totalBranches = headOffices.reduce((sum, ho) => sum + (ho.total_branches || 0), 0);
  const activeBranches = headOffices.reduce((sum, ho) => sum + (ho.active_branches || 0), 0);

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Head Office Management</h1>
          <Link
            to="/head-office/list"
            className="bg-orange1 text-white px-4 py-2 rounded hover:bg-orange-600 transition"
          >
            View All Head Offices
          </Link>
        </div>

        {isLoading ? (
          <Spinner loading={isLoading} />
        ) : error ? (
          <div className="text-red-500 bg-red-50 border border-red-200 rounded p-4">
            <h3 className="font-bold mb-2">Error loading dashboard</h3>
            <p>{error?.message || "An error occurred"}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Head Offices</p>
                <h3 className="text-2xl font-bold mt-2">{totalHeadOffices}</h3>
              </div>
              <BuildingsFill className="w-10 h-10 text-orange1" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Active Head Offices</p>
                <h3 className="text-2xl font-bold mt-2 text-green-600">{activeHeadOffices}</h3>
              </div>
              <BuildingsFill className="w-10 h-10 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Branches</p>
                <h3 className="text-2xl font-bold mt-2">{totalBranches}</h3>
              </div>
              <People className="w-10 h-10 text-blue-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Active Branches</p>
                <h3 className="text-2xl font-bold mt-2 text-green-600">{activeBranches}</h3>
              </div>
              <People className="w-10 h-10 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Recent Head Offices</h2>
          </div>
          {headOffices.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500 mb-4">No head offices found</p>
              <Link
                to="/head-office/create"
                className="inline-block bg-orange1 text-white px-6 py-2 rounded hover:bg-orange-600 transition"
              >
                Create First Head Office
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Branches
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {headOffices.slice(0, 5).map((headOffice) => (
                  <tr key={headOffice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{headOffice.name}</div>
                      <div className="text-sm text-gray-500">{headOffice.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {headOffice.city}, {headOffice.state}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {headOffice.active_branches}/{headOffice.total_branches}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          headOffice.is_activated
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {headOffice.is_activated ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link
                        to={`/head-office/details?id=${headOffice.id}`}
                        className="text-orange1 hover:text-orange-600"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </MainLayouts>
  );
};

export default HeadOfficeDashboard;
